/** @jsxImportSource @emotion/react */
import React from 'react';
import { css } from '@emotion/react';
import { Space, Typography, Tag, Button, Popconfirm, Switch, InputNumber, Divider, DatePicker } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { HeatingConfig } from '../../api/automationApi';

const { Text } = Typography;

const styles = {
  panel: css({
    width: '100%',
    border: '1px solid #d9e6f2',
    borderRadius: 8,
    overflow: 'hidden',
    marginTop: 8,
    background: '#f0f7ff',
  }),
  header: css({
    padding: '10px 16px',
    background: '#e6f0fa',
    borderBottom: '1px solid #d9e6f2',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  body: css({
    padding: '12px 16px',
  }),
  section: css({
    marginBottom: 12,
  }),
  sectionLabel: css({
    fontSize: 12,
    color: '#8c8c8c',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: 6,
    display: 'block',
  }),
  configRow: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
    fontSize: 13,
  }),
};

const COVERING_LABELS: Record<string, string> = {
  greenhouse: 'Теплица',
  parnik: 'Парник',
  geotextile: 'Геотекстиль',
};

export interface GreenhouseOptions {
  autoVentilation: boolean;
  ventilationEnabled?: boolean;
  autoHeating: boolean;
  ventilationMaxC?: number | null;
}

interface GreenhouseOptionsPanelProps {
  subtype: string;
  cellCount: number;
  onDelete?: () => void;
  disabled?: boolean;
  /** Текущее состояние отопления */
  heatingEnabled?: boolean;
  heatingConfig?: HeatingConfig | null;
  /** Callback при переключении отопления */
  onHeatingToggle?: (enabled: boolean) => void;
  /** Callback при изменении параметров отопления */
  onHeatingConfigChange?: (config: HeatingConfig) => void;
  /** Structural greenhouse parameters */
  options?: GreenhouseOptions;
  onParamChange?: (params: Partial<GreenhouseOptions>) => void;
  /** Окно «действует с / по» (YYYY-MM-DD | null) */
  activeFrom?: string | null;
  activeTo?: string | null;
  /** Callback при изменении окна установки/демонтажа */
  onActiveWindowChange?: (activeFrom: string | null, activeTo: string | null) => void;
}

const GreenhouseOptionsPanel: React.FC<GreenhouseOptionsPanelProps> = ({
  subtype,
  cellCount,
  onDelete,
  disabled = false,
  heatingEnabled = false,
  heatingConfig,
  onHeatingToggle,
  onHeatingConfigChange,
  options,
  onParamChange,
  activeFrom = null,
  activeTo = null,
  onActiveWindowChange,
}) => {
  const canHeat = subtype === 'greenhouse';
  const canVent = subtype === 'greenhouse';
  const autoVentilation = options?.autoVentilation === true;

  return (
    <div css={styles.panel}>
      <div css={styles.header}>
        <Space size={8}>
          <Text strong>{COVERING_LABELS[subtype] || subtype}</Text>
          <Tag color="blue">{cellCount} кл.</Tag>
        </Space>
      </div>
      <div css={styles.body}>
        {/* Install / removal window — when the covering affects beds */}
        {onActiveWindowChange && (
          <div css={styles.section}>
            <span css={styles.sectionLabel}>Действует</span>
            <div css={styles.configRow}>
              <Text type="secondary">С даты</Text>
              <DatePicker
                size="small"
                style={{ width: 130 }}
                placeholder="с начала"
                value={activeFrom ? dayjs(activeFrom) : null}
                disabled={disabled}
                onChange={(d) => onActiveWindowChange(d ? d.format('YYYY-MM-DD') : null, activeTo ?? null)}
              />
            </div>
            <div css={styles.configRow}>
              <Text type="secondary">По дату</Text>
              <DatePicker
                size="small"
                style={{ width: 130 }}
                placeholder="бессрочно"
                value={activeTo ? dayjs(activeTo) : null}
                disabled={disabled}
                onChange={(d) => onActiveWindowChange(activeFrom ?? null, d ? d.format('YYYY-MM-DD') : null)}
              />
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              До даты установки грядка живёт как открытый грунт.
            </Text>
          </div>
        )}

        {onActiveWindowChange && canHeat && onHeatingToggle && <Divider style={{ margin: '8px 0' }} />}

        {canVent && onParamChange && (
          <div css={styles.section}>
            <span css={styles.sectionLabel}>Проветривание</span>
            <div css={styles.configRow}>
              <Text>Автоматически</Text>
              <Switch
                size="small"
                checked={autoVentilation}
                onChange={(checked) => onParamChange({
                  autoVentilation: checked,
                  ventilationEnabled: checked,
                  ventilationMaxC: options?.ventilationMaxC ?? 35,
                })}
                disabled={disabled}
              />
            </div>
            {autoVentilation && (
              <div css={styles.configRow}>
                <Text type="secondary">Порог (°C)</Text>
                <InputNumber
                  size="small"
                  style={{ width: 80 }}
                  value={options?.ventilationMaxC ?? 35}
                  min={-40}
                  max={80}
                  disabled={disabled}
                  onChange={(v) => onParamChange({ ventilationMaxC: v ?? 35 })}
                />
              </div>
            )}
          </div>
        )}

        {canVent && onParamChange && canHeat && onHeatingToggle && <Divider style={{ margin: '8px 0' }} />}

        {/* Heating controls — only for greenhouse subtype */}
        {canHeat && onHeatingToggle && (
          <div css={styles.section}>
            <span css={styles.sectionLabel}>Отопление</span>
            <div css={styles.configRow}>
              <Text>Включено</Text>
              <Switch
                size="small"
                checked={heatingEnabled}
                onChange={onHeatingToggle}
                disabled={disabled}
              />
            </div>
            {heatingEnabled && onHeatingConfigChange && (
              <>
                <div css={styles.configRow}>
                  <Text type="secondary">Мин. темп. (°C)</Text>
                  <InputNumber
                    size="small"
                    style={{ width: 80 }}
                    value={heatingConfig?.minC ?? 5}
                    min={-10}
                    max={40}
                    disabled={disabled}
                    onChange={(v) =>
                      onHeatingConfigChange({ ...heatingConfig, minC: v ?? 5 })
                    }
                  />
                </div>
                <div css={styles.configRow}>
                  <Text type="secondary">Макс. темп. (°C)</Text>
                  <InputNumber
                    size="small"
                    style={{ width: 80 }}
                    value={heatingConfig?.maxC ?? 25}
                    min={0}
                    max={50}
                    disabled={disabled}
                    onChange={(v) =>
                      onHeatingConfigChange({ ...heatingConfig, maxC: v ?? 25 })
                    }
                  />
                </div>
                <div css={styles.configRow}>
                  <Text type="secondary">Вентиляция (°C)</Text>
                  <InputNumber
                    size="small"
                    style={{ width: 80 }}
                    value={heatingConfig?.ventilationMaxC ?? 35}
                    min={15}
                    max={60}
                    disabled={disabled}
                    onChange={(v) =>
                      onHeatingConfigChange({ ...heatingConfig, ventilationMaxC: v ?? 35, ventilationEnabled: true })
                    }
                  />
                </div>
              </>
            )}
          </div>
        )}

        {canHeat && onHeatingToggle && onDelete && <Divider style={{ margin: '8px 0' }} />}

        {onDelete && (
          <Popconfirm
            title={`Удалить ${COVERING_LABELS[subtype] || subtype}?`}
            description="Покрытие будет снято со всex ячеек. Грядки и дорожки внутри сохранятся."
            onConfirm={onDelete}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
            disabled={disabled}
          >
            <Button
              danger
              size="small"
              block
              icon={<DeleteOutlined />}
              disabled={disabled}
            >
              Удалить {COVERING_LABELS[subtype] || subtype}
            </Button>
          </Popconfirm>
        )}
      </div>
    </div>
  );
};

export default GreenhouseOptionsPanel;
